/**
 * Bounded ring buffer of recent console errors / uncaught exceptions,
 * captured for the in-app bug reporter (BugReporterPanel). `install()` is
 * called once at app boot (src/index.tsx).
 *
 * Additive only: `console.error` is wrapped, never replaced — the original
 * always still runs. Capture never throws past its own try/catch so a bug in
 * the buffer itself cannot break app logging or crash the app.
 */

const MAX_ENTRIES = 50;
const MAX_ENTRY_LENGTH = 2000;

let buffer: string[] = [];
let installed = false;

/** Render a console.error/console.warn argument list to a single bounded string. */
export function formatConsoleArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      if (typeof arg === 'string') return arg;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ')
    .slice(0, MAX_ENTRY_LENGTH);
}

/** Append an entry to the ring buffer, evicting the oldest when over capacity. */
export function pushError(entry: string): void {
  buffer.push(`[${new Date().toISOString()}] ${entry}`);
  if (buffer.length > MAX_ENTRIES) {
    buffer = buffer.slice(buffer.length - MAX_ENTRIES);
  }
}

/** Snapshot of the buffer, oldest first. */
export function getRecentErrors(): string[] {
  return [...buffer];
}

/** Reset the buffer — used by tests and available for a manual "clear" action. */
export function clearErrors(): void {
  buffer = [];
}

/** Wrap console.error and add window error listeners. Safe to call more than once or outside a browser. */
export function install(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      pushError(formatConsoleArgs(args));
    } catch {
      // Capture must never break logging.
    }
    originalConsoleError(...args);
  };

  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      pushError(`Uncaught: ${event.message} (${event.filename}:${event.lineno})`);
    } catch {
      // noop
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason instanceof Error ? event.reason.message : String(event.reason);
      pushError(`Unhandled promise rejection: ${reason}`);
    } catch {
      // noop
    }
  });
}
